import { useEffect, type ReactNode } from "react";

import type { Density } from "@/hooks/useDensity";
import type { Theme } from "@/hooks/useTheme";

type Surface = Exclude<Theme, "system">;

interface ThemeScopeProps {
  theme: Surface;
  density?: Density;
  children: ReactNode;
}

/**
 * Per-route theme driver. The Daylight Wayfinder app is surface-themed by
 * ROUTE, not by user preference: the visitor surfaces (/login, /) render
 * light + spacious; /docs renders dark. We therefore set `data-theme` /
 * `data-density` two ways:
 *
 *   1. on <html> (effect) — so the global `body { @apply bg-background }`
 *      base rule paints the correct surface colour edge-to-edge; and
 *   2. on a wrapping <div> — so the OKLCH custom properties cascade locally
 *      and children render correctly even before the effect commits.
 *
 * Because tokens.css targets the `[data-theme=...]` attribute selector (not
 * just :root), both placements resolve the same variable ladder. This keeps
 * the no-ThemeProvider, attribute-driven model ported from Kalista.
 */
export function ThemeScope({ theme, density = "spacious", children }: ThemeScopeProps) {
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.setAttribute("data-density", density);
    root.classList.toggle("dark", theme === "dark");
  }, [theme, density]);

  return (
    <div
      data-theme={theme}
      data-density={density}
      className="min-h-screen bg-background text-foreground"
    >
      {children}
    </div>
  );
}
